import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { usePageSEO } from "../lib/seo";
import { ArrowRight, Search, Tag } from "lucide-react";
import { motion } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;

export default function InsightsPage() {
  usePageSEO({ title: "Insights", description: "AI growth strategies, automation frameworks, and revenue systems for SMB founders. Actionable insights from Stansbury AI.", path: "/insights" });
  const [articles, setArticles] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeTag, setActiveTag] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const tagParam = activeTag ? `?tag=${activeTag}` : "";
        const [artRes, tagRes] = await Promise.all([
          fetch(`${API}/api/insights/published${tagParam}`),
          fetch(`${API}/api/insights/tags`),
        ]);
        setArticles(await artRes.json());
        setTags(await tagRes.json());
      } catch (e) { console.error("Insights load failed:", e); }
      finally { setLoading(false); }
    };
    load();
  }, [activeTag]);

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="insights-page">
      {/* Nav */}
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16">
          <Link to="/"><img src="/stansbury-logo.png" alt="Stansbury AI" className="h-10 sm:h-12" /></Link>
          <div className="flex items-center gap-6">
            <Link to="/insights" className="text-sm font-['Outfit'] font-medium text-[#0033CC]">Insights</Link>
            <Link to="/blueprint" className="bg-[#0033CC] text-white px-5 py-2 text-sm font-['Outfit'] font-medium hover:bg-[#002299] transition-colors">Get Your Blueprint</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#0F172A] py-16 md:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.25em] text-[#818CF8] mb-4">Stansbury AI Insights</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-medium text-white tracking-tight leading-[1.1] mb-4">
            Insights That Turn<br />AI Into Revenue
          </h1>
          <p className="text-base text-slate-400 font-['IBM_Plex_Sans'] max-w-lg mx-auto leading-relaxed">
            Strategies, frameworks, and playbooks for founders building AI-powered growth engines.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        {/* Tag Filters */}
        {tags.length > 0 && (
          <div className="flex items-center gap-2 mb-8 flex-wrap" data-testid="tag-filters">
            <Tag className="w-3.5 h-3.5 text-slate-400" />
            <button onClick={() => setActiveTag("")} className={`text-xs font-['Outfit'] px-3 py-1 transition-colors ${!activeTag ? "bg-[#0033CC] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0033CC]"}`} data-testid="tag-all">All</button>
            {tags.map(t => (
              <button key={t.tag} onClick={() => setActiveTag(t.tag)} className={`text-xs font-['Outfit'] px-3 py-1 transition-colors ${activeTag === t.tag ? "bg-[#0033CC] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0033CC]"}`} data-testid={`tag-${t.tag}`}>
                {t.tag} <span className="opacity-50 ml-1">{t.count}</span>
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20"><div className="w-6 h-6 border-2 border-[#0033CC] border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-lg font-['Outfit'] text-slate-400">No articles yet. Check back soon.</p>
          </div>
        ) : (
          <>
            {/* Featured Article */}
            {featured && (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-12" data-testid="featured-article">
                <Link to={`/insights/${featured.slug}`} className="group block bg-white border border-slate-200 hover:border-[#0033CC]/30 transition-colors">
                  {featured.featured_image && <div className="h-64 bg-cover bg-center" style={{ backgroundImage: `url(${featured.featured_image})` }} />}
                  <div className="p-8 md:p-10">
                    <div className="flex items-center gap-2 mb-3">
                      {featured.tags?.slice(0, 3).map(tag => (
                        <span key={tag} className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#0033CC] bg-[#0033CC]/5 px-2 py-0.5">{tag}</span>
                      ))}
                      <span className="text-xs text-slate-400 font-['IBM_Plex_Sans'] ml-auto">{featured.published_at ? new Date(featured.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : ""}</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-['Outfit'] font-medium text-[#0F172A] tracking-tight mb-3 group-hover:text-[#0033CC] transition-colors">{featured.title}</h2>
                    {featured.subtitle && <p className="text-base text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed mb-4">{featured.subtitle}</p>}
                    <span className="inline-flex items-center text-sm font-['Outfit'] font-medium text-[#0033CC] group-hover:gap-2 transition-all gap-1">
                      Read Article <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            )}

            {/* Article Grid */}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="article-grid">
                {rest.map((article, i) => (
                  <motion.div key={article.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                    <Link to={`/insights/${article.slug}`} className="group block bg-white border border-slate-200 hover:border-[#0033CC]/30 transition-colors h-full" data-testid={`article-card-${i}`}>
                      {article.featured_image && <div className="h-40 bg-cover bg-center" style={{ backgroundImage: `url(${article.featured_image})` }} />}
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          {article.tags?.slice(0, 2).map(tag => (
                            <span key={tag} className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#0033CC] bg-[#0033CC]/5 px-2 py-0.5">{tag}</span>
                          ))}
                        </div>
                        <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] tracking-tight mb-2 group-hover:text-[#0033CC] transition-colors leading-snug">{article.title}</h3>
                        {article.subtitle && <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed line-clamp-2 mb-3">{article.subtitle}</p>}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{article.published_at ? new Date(article.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#0033CC] transition-colors" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Bottom CTA */}
        <div className="mt-16 bg-[#0033CC] p-10 text-center">
          <h2 className="text-2xl font-['Outfit'] font-medium text-white mb-3">Ready to see where AI can drive revenue?</h2>
          <p className="text-sm text-white/70 font-['IBM_Plex_Sans'] mb-6 max-w-md mx-auto">Get your AI Growth Blueprint — a comprehensive diagnostic that reveals exactly where to deploy AI for maximum impact.</p>
          <Link to="/blueprint" className="inline-block bg-white text-[#0033CC] px-8 py-3 font-['Outfit'] font-semibold text-sm hover:bg-slate-100 transition-colors" data-testid="insights-cta">
            Get Your Blueprint <ArrowRight className="w-4 h-4 inline ml-1" />
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 px-6 text-center">
        <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">Stansbury AI &middot; <Link to="/privacy" className="hover:text-[#0033CC]">Privacy</Link> &middot; <Link to="/contact" className="hover:text-[#0033CC]">Contact</Link></p>
      </footer>
    </div>
  );
}
